import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';
import { Icon, type IconName } from '../icons/Icon.js';
import { cx } from '../utils/cx.js';
import { Spinner } from './Spinner.js';

/**
 * C-01 主 / C-02 副 / C-03 危険 / ghost。
 * 状態: 通常 / 押下（1 段暗く）/ 無効（本体 38% + 理由は 100% の文字）/ 処理中（スピナー + 文字は残す）。
 * 高さはデスクトップ 44、タッチ 48（--tap-min）。lg は主要動作の 56。
 */
export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-brand-on border-brand hover:bg-brand-pressed active:bg-brand-pressed',
  secondary: 'bg-card text-fg border-line-strong hover:bg-sunken active:bg-sunken',
  danger: 'bg-danger text-state-on border-danger hover:bg-danger-pressed active:bg-danger-pressed',
  ghost: 'bg-transparent text-fg border-transparent hover:bg-sunken active:bg-sunken',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'min-tap px-3 gap-1.5',
  md: 'min-tap px-5 gap-2',
  lg: 'min-h-14 px-6 gap-2',
};

export function buttonClasses(variant: ButtonVariant = 'primary', size: ButtonSize = 'md', block = false, extra?: string): string {
  return cx(
    'inline-flex items-center justify-center rounded-input border text-button select-none transition-colors',
    'disabled:disabled-look aria-disabled:disabled-look',
    VARIANT[variant],
    SIZE[size],
    block && 'w-full',
    extra,
  );
}

export interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  block?: boolean;
  icon?: IconName;
  iconEnd?: IconName;
  /** 無効のとき、近くに 100% の文字で出す理由（05 §4.1） */
  disabledReason?: ReactNode;
}

export function Button({ variant = 'primary', size = 'md', loading = false, block = false, icon, iconEnd, disabledReason, className, children, disabled, type = 'button', ...rest }: ButtonProps) {
  const isDisabled = disabled || loading;
  const btn = (
    <button type={type} className={buttonClasses(variant, size, block, className)} disabled={isDisabled} aria-busy={loading || undefined} {...rest}>
      {loading ? <Spinner size={20} /> : icon ? <Icon name={icon} size={20} /> : null}
      <span className="wrap-name">{children}</span>
      {iconEnd && !loading ? <Icon name={iconEnd} size={20} /> : null}
    </button>
  );
  if (disabledReason && isDisabled) {
    return (
      <span className={cx('inline-flex flex-col gap-1', block && 'w-full')}>
        {btn}
        <span className="text-caption text-fg-secondary">{disabledReason}</span>
      </span>
    );
  }
  return btn;
}

export interface LinkButtonProps extends LinkProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  icon?: IconName;
  iconEnd?: IconName;
}

export function LinkButton({ variant = 'secondary', size = 'md', block = false, icon, iconEnd, className, children, ...rest }: LinkButtonProps) {
  return (
    <Link className={buttonClasses(variant, size, block, className)} {...rest}>
      {icon ? <Icon name={icon} size={20} /> : null}
      <span className="wrap-name">{children}</span>
      {iconEnd ? <Icon name={iconEnd} size={20} /> : null}
    </Link>
  );
}

/**
 * アイコンだけのボタン（05 §4.4 の例外にだけ使う）。読み上げラベル必須。
 */
export interface IconButtonProps extends Omit<ComponentPropsWithoutRef<'button'>, 'children'> {
  icon: IconName;
  label: string;
  size?: 20 | 24 | 28;
  variant?: 'ghost' | 'secondary';
}

export function IconButton({ icon, label, size = 24, variant = 'ghost', className, type = 'button', ...rest }: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex shrink-0 items-center justify-center rounded-input transition-colors disabled:disabled-look',
        'min-tap min-w-[var(--tap-min)]',
        variant === 'ghost' ? 'text-fg hover:bg-sunken' : 'border border-line-strong bg-card text-fg hover:bg-sunken',
        className,
      )}
      {...rest}
    >
      <Icon name={icon} size={size} />
    </button>
  );
}
